/**
 * EditorDock — menú lateral de secciones independientes para el Map Editor.
 *
 * Cada sección es un "cajón" anclado a un costado de la pantalla con:
 *  - una mini-pestaña (spine) SIEMPRE visible con flecha de toggle + título vertical,
 *  - un panel que se desliza horizontalmente para adentro/afuera (toggle POR SECCIÓN),
 *  - un botón "pin" que la despega como ventana flotante movible (✕ la re-acopla),
 *  - flip global de lado (izquierda/derecha) que reordena todos los cajones.
 *
 * Es agnóstico del dominio: sólo recibe un `body: HTMLElement` ya construido y lo aloja.
 */
import { domDragCursorCss } from '../cursors';
import { attachCustomScrollbar } from '../customScrollbar';

const STORAGE_KEY = 'map_editor_dock_v2';

const SPINE_W = 28; // ancho de la mini-pestaña persistente
const BODY_MAX_HEIGHT = '62vh';
const STYLE_TAG_ID = 'editor-dock-styles';

export interface DockSectionOptions {
  /** Identificador estable (para persistencia). Ej: 'npcs' | 'areas' | 'surfaces'. */
  id: string;
  /** Título visible (puede incluir emoji). Ej: '🎨 Superficies'. */
  title: string;
  /** El content div EXISTENTE; su contenido no se modifica. */
  body: HTMLElement;
  /** Ancho del panel cuando está abierto/flotante. */
  preferredWidth: number;
  /** Si arranca cerrado/deslizado (sólo si no hay estado persistido previo). */
  defaultCollapsed?: boolean;
}

interface DockSectionState {
  open: boolean; // panel desplegado (false = deslizado, sólo mini-pestaña)
  floating: boolean;
  floatPos: { top: number; left: number } | null;
}

interface DockState {
  side: 'left' | 'right';
  sections: Record<string, DockSectionState>;
}

interface DockSection {
  id: string;
  preferredWidth: number;
  order: number;
  sectionEl: HTMLDivElement;
  panel: HTMLDivElement;
  headerEl: HTMLDivElement;
  spineArrow: HTMLSpanElement;
  actionBtn: HTMLButtonElement; // pin (acoplada) / ✕ (flotante)
  state: DockSectionState;
}

export class EditorDock {
  private readonly host: HTMLElement;
  private readonly root: HTMLDivElement;
  private readonly sections: DockSection[] = [];
  private state: DockState;
  private zCounter = 40;

  // Drag compartido para ventanas flotantes.
  private dragging: DockSection | null = null;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private readonly onMouseMove: (ev: MouseEvent) => void;
  private readonly onMouseUp: () => void;

  constructor(host: HTMLElement) {
    this.host = host;
    this.state = this.loadState();

    EditorDock.injectStyles();

    // Contenedor raíz transparente (no bloquea clicks del mapa en los huecos).
    this.root = document.createElement('div');
    this.root.className = 'editor-dock-root';

    // Barra superior con el botón de flip de lado.
    const flipBar = document.createElement('div');
    flipBar.className = 'editor-dock-flipbar';
    const flipBtn = EditorDock.makeIconButton('⇄', 'Cambiar de lado');
    flipBtn.addEventListener('click', () => {
      this.state.side = this.state.side === 'left' ? 'right' : 'left';
      this.applySide();
      this.persist();
    });
    flipBar.appendChild(flipBtn);

    this.root.appendChild(flipBar);
    this.host.appendChild(this.root);

    this.onMouseMove = (ev: MouseEvent): void => this.handleDragMove(ev);
    this.onMouseUp = (): void => {
      this.dragging = null;
    };
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);

    this.applySide();
  }

  /** Registra una sección. El orden de las llamadas define el orden vertical. */
  addSection(opts: DockSectionOptions): void {
    const order = this.sections.length;

    const saved = this.state.sections[opts.id];
    const sectionState: DockSectionState = saved ?? {
      open: !(opts.defaultCollapsed ?? false),
      floating: false,
      floatPos: null,
    };
    this.state.sections[opts.id] = sectionState;

    // --- Sección ---
    const sectionEl = document.createElement('div');
    sectionEl.className = 'editor-dock-section';

    // Mini-pestaña (spine) persistente: flecha de toggle + título vertical.
    const spine = document.createElement('div');
    spine.className = 'editor-dock-spine';

    const spineArrow = document.createElement('span');
    spineArrow.className = 'editor-dock-spine-arrow';

    const spineTitle = document.createElement('span');
    spineTitle.className = 'editor-dock-spine-title';
    spineTitle.textContent = opts.title;

    spine.append(spineArrow, spineTitle);

    // Panel desplegable.
    const panel = document.createElement('div');
    panel.className = 'editor-dock-panel';

    const panelInner = document.createElement('div');
    panelInner.className = 'editor-dock-panel-inner';
    panelInner.style.width = `${opts.preferredWidth}px`;

    const headerEl = document.createElement('div');
    headerEl.className = 'editor-dock-section-header';

    const titleEl = document.createElement('span');
    titleEl.className = 'editor-dock-section-title';
    titleEl.textContent = opts.title;

    const spacer = document.createElement('span');
    spacer.style.flex = '1';

    const actionBtn = EditorDock.makeIconButton('📌', 'Desacoplar (flotante)');

    headerEl.append(titleEl, spacer, actionBtn);

    opts.body.classList.add('editor-dock-body', 'editor-dock-scroll');
    opts.body.style.maxHeight = BODY_MAX_HEIGHT;

    panelInner.append(headerEl, opts.body);
    // Scrollbar dorado custom (reemplaza al nativo). Ver client/src/ui/customScrollbar.ts.
    attachCustomScrollbar(opts.body);
    panel.appendChild(panelInner);
    sectionEl.append(spine, panel);

    const section: DockSection = {
      id: opts.id,
      preferredWidth: opts.preferredWidth,
      order,
      sectionEl,
      panel,
      headerEl,
      spineArrow,
      actionBtn,
      state: sectionState,
    };
    this.sections.push(section);

    // --- Interacciones ---
    // Click en la spine togglea el deslizado del panel.
    spine.addEventListener('click', () => {
      this.setOpen(section, !section.state.open);
    });

    // Pin / re-acople.
    actionBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.setFloating(section, !section.state.floating);
    });

    // Drag de la ventana flotante por el header + traer al frente.
    headerEl.addEventListener('mousedown', (ev) => {
      if (!section.state.floating) return;
      this.bringToFront(section);
      const target = ev.target as HTMLElement;
      if (target.closest('button')) return;
      this.dragging = section;
      const rect = section.sectionEl.getBoundingClientRect();
      this.dragOffsetX = ev.clientX - rect.left;
      this.dragOffsetY = ev.clientY - rect.top;
      ev.preventDefault();
    });

    // Estado inicial.
    if (section.state.floating) {
      this.mountFloating(section);
    } else {
      this.mountDocked(section);
    }
    this.applyOpenVisual(section);
    this.applySectionSide(section);
  }

  destroy(): void {
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.root.remove();
    for (const s of this.sections) s.sectionEl.remove();
  }

  // ===================================================================
  // Deslizado (toggle por sección)
  // ===================================================================

  private setOpen(section: DockSection, open: boolean): void {
    section.state.open = open;
    this.applyOpenVisual(section);
    this.persist();
  }

  private applyOpenVisual(section: DockSection): void {
    const showPanel = section.state.floating ? true : section.state.open;
    section.sectionEl.classList.toggle('is-open', showPanel);
    section.panel.style.width = showPanel ? `${section.preferredWidth}px` : '0px';
    section.spineArrow.textContent = this.arrowGlyph(section.state.open);
  }

  /** Flecha de la spine: apunta hacia el borde para cerrar, hacia afuera para abrir. */
  private arrowGlyph(open: boolean): string {
    const left = this.state.side === 'left';
    if (left) return open ? '‹' : '›';
    return open ? '›' : '‹';
  }

  // ===================================================================
  // Acoplado <-> Flotante
  // ===================================================================

  private setFloating(section: DockSection, floating: boolean): void {
    if (section.state.floating === floating) return;
    section.state.floating = floating;
    if (floating) {
      this.mountFloating(section);
    } else {
      this.mountDocked(section);
    }
    this.applyOpenVisual(section);
    this.applySectionSide(section);
    this.persist();
  }

  private mountDocked(section: DockSection): void {
    const el = section.sectionEl;
    el.classList.remove('is-floating');
    el.style.position = '';
    el.style.left = '';
    el.style.top = '';
    el.style.zIndex = '';
    section.actionBtn.textContent = '📌';
    section.actionBtn.title = 'Desacoplar (flotante)';

    // Reinsertar respetando el orden original entre las acopladas.
    const reference = this.sections
      .filter((s) => !s.state.floating && s.order > section.order)
      .sort((a, b) => a.order - b.order)[0];
    if (reference) {
      this.root.insertBefore(el, reference.sectionEl);
    } else {
      this.root.appendChild(el);
    }
  }

  private mountFloating(section: DockSection): void {
    const el = section.sectionEl;
    el.classList.add('is-floating');
    el.style.position = 'absolute';

    const pos = section.state.floatPos ?? {
      top: 80 + section.order * 32,
      left: 96 + section.order * 32,
    };
    section.state.floatPos = pos;
    el.style.left = `${pos.left}px`;
    el.style.top = `${pos.top}px`;

    section.actionBtn.textContent = '✕';
    section.actionBtn.title = 'Re-acoplar al menú';

    this.host.appendChild(el);
    this.bringToFront(section);
  }

  private bringToFront(section: DockSection): void {
    section.sectionEl.style.zIndex = String(++this.zCounter);
  }

  // ===================================================================
  // Drag de flotantes
  // ===================================================================

  private handleDragMove(ev: MouseEvent): void {
    const section = this.dragging;
    if (!section) return;
    const hostRect = this.host.getBoundingClientRect();
    const el = section.sectionEl;
    let left = ev.clientX - this.dragOffsetX - hostRect.left;
    let top = ev.clientY - this.dragOffsetY - hostRect.top;
    left = Math.max(0, Math.min(left, hostRect.width - el.offsetWidth));
    top = Math.max(0, Math.min(top, hostRect.height - el.offsetHeight));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    section.state.floatPos = { top, left };
    this.persist();
  }

  // ===================================================================
  // Lado (flip izquierda/derecha)
  // ===================================================================

  private applySide(): void {
    const left = this.state.side === 'left';
    this.root.classList.toggle('side-left', left);
    this.root.classList.toggle('side-right', !left);
    for (const s of this.sections) {
      this.applySectionSide(s);
      this.applyOpenVisual(s); // la flecha depende del lado
    }
  }

  private applySectionSide(section: DockSection): void {
    const left = this.state.side === 'left';
    section.sectionEl.classList.toggle('side-left', left);
    section.sectionEl.classList.toggle('side-right', !left);
  }

  // ===================================================================
  // Persistencia
  // ===================================================================

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // localStorage no disponible: ignorar.
    }
  }

  private loadState(): DockState {
    const fallback: DockState = { side: 'left', sections: {} };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw) as Partial<DockState>;
      return {
        side: parsed.side === 'right' ? 'right' : 'left',
        sections: parsed.sections ?? {},
      };
    } catch {
      return fallback;
    }
  }

  // ===================================================================
  // Helpers estáticos
  // ===================================================================

  private static makeIconButton(glyph: string, title: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'editor-dock-icon-btn';
    btn.textContent = glyph;
    btn.title = title;
    return btn;
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_TAG_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_TAG_ID;
    style.textContent = `
.editor-dock-root {
  position: absolute;
  top: 56px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 15;
  pointer-events: none;            /* los huecos dejan pasar clicks al mapa */
  color: #e8e8e8;
  font-family: inherit;
}
.editor-dock-root.side-left { left: 12px; align-items: flex-start; }
.editor-dock-root.side-right { right: 12px; align-items: flex-end; }

.editor-dock-flipbar { pointer-events: auto; }

.editor-dock-section {
  display: flex;
  background: #181818;
  border: 1px solid #383838;
  border-radius: 7px;
  overflow: hidden;
  pointer-events: auto;
  max-height: calc(100vh - 120px);
}
.editor-dock-section.side-left { flex-direction: row; }
.editor-dock-section.side-right { flex-direction: row-reverse; }
.editor-dock-section.is-floating {
  box-shadow: 0 10px 30px rgba(0,0,0,0.7);
  border-color: #4a4a4a;
}
.editor-dock-section.is-floating .editor-dock-spine { display: none; }

/* --- Mini-pestaña (spine) --- */
.editor-dock-spine {
  flex: 0 0 ${SPINE_W}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  background: linear-gradient(180deg,#2c2c2c,#222);
  cursor: pointer;
  user-select: none;
}
.editor-dock-spine:hover { background: #343434; }
.editor-dock-spine-arrow { font-size: 15px; line-height: 1; color: #cfcfcf; }
.editor-dock-spine-title {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .02em;
  color: #c8c8c8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 100%;
}
.editor-dock-section.is-open .editor-dock-spine-title { display: none; }

/* --- Panel desplegable --- */
.editor-dock-panel {
  flex: 0 0 auto;
  overflow: hidden;
  transition: width .25s cubic-bezier(.22,.61,.36,1);
  display: flex;
}
.editor-dock-section.side-left .editor-dock-panel { border-left: 1px solid #303030; }
.editor-dock-section.side-right .editor-dock-panel { border-right: 1px solid #303030; }
.editor-dock-panel-inner {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.editor-dock-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: #262626;
  user-select: none;
  cursor: default;
}
.editor-dock-section.is-floating .editor-dock-section-header { cursor: ${domDragCursorCss()}; }
.editor-dock-section-title {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.editor-dock-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 5px;
  background: #2b2b2b;
  color: #cfcfcf;
  border: 1px solid #3a3a3a;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
}
.editor-dock-icon-btn:hover { background: #383838; color: #fff; }

.editor-dock-body { overflow: auto; }
/* El scrollbar lo dibuja el overlay dorado (customScrollbar.ts); el nativo se
   oculta vía la clase .aureum-scroll-host que aplica attachCustomScrollbar. */
`;
    document.head.appendChild(style);
  }
}
